import { Button, Currency, Message, Modal, Table } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { formatMonetaire } from '@/utils';

export interface ModalRepartitionNumeraireProps {
  onClose: () => void;
  onSave: (montants: Record<string, number>[]) => void;
  montantTotalReglements: number;
}

interface MontantNumeraire {
  valeur: number;
  nombre: number | string;
  montant: number;
  enterIndex: number;
}

const VALEURS_MONETAIRES = [
  200.0,
  100.0,
  50.0,
  20.0,
  10.0,
  5.0, // Billets
  2.0,
  1.0,
  0.5,
  0.2,
  0.1,
  0.05,
  0.02,
  0.01, // Pièces
];

const ModalRepartitionNumeraire = (props: ModalRepartitionNumeraireProps) => {
  const { montantTotalReglements, onClose, onSave } = props;

  // #region Déclarations

  const [montants, setMontants] = React.useState<MontantNumeraire[]>([]);

  let timeout;

  // #endregion

  // #region Fonctions

  const handleChangeMontant = React.useCallback((index: number, value: string) => {
    setMontants((prev) => {
      const res = _.cloneDeep(prev);
      const nombre = value === '' || value === null ? 0 : parseFloat(value);
      res[index].nombre = value === '' || value === null ? '' : nombre;
      res[index].montant = nombre * res[index].valeur;
      return res;
    });
  }, []);

  const handleKeyPress = (e) => {
    const {
      key,
      target: { id },
    } = e;

    const indice = parseInt(id.split('@')[1], 10);
    let nextIndice = indice;

    if (key === 'Enter') {
      nextIndice = e.shiftKey ? indice - 1 : indice + 1;
    }

    if (key === 'ArrowUp') {
      nextIndice = indice - 1;
    }

    if (key === 'ArrowDown') {
      nextIndice = indice + 1;
    }

    if (nextIndice >= 0 && nextIndice < montants.length) {
      const nextElement = document.getElementById(`currency@${nextIndice}`) as HTMLInputElement;

      if (nextElement) {
        nextElement.focus();
      }
    }
  };

  const handleValidate = React.useCallback(() => {
    if (onSave) {
      onSave(
        montants.map((m) => ({
          valeur: m.valeur,
          nombre: typeof m.nombre === 'string' ? 0 : m.nombre,
          montant: m.montant,
        })),
      );
    }
  }, [montants, onSave]);

  const totalBillets = React.useMemo(() => _.sumBy(montants.slice(0, 6), 'montant'), [montants]);

  const totalPieces = React.useMemo(() => _.sumBy(montants.slice(6), 'montant'), [montants]);

  const totalVersement = React.useMemo(
    () => totalBillets + totalPieces,
    [totalBillets, totalPieces],
  );

  // #endregion

  // #region Effets

  React.useEffect(() => {
    const res: MontantNumeraire[] = VALEURS_MONETAIRES.map((valeur, index) => ({
      valeur,
      nombre: '',
      montant: 0,
      enterIndex: index,
    }));

    setMontants(res);
  }, []);

  // #endregion

  return (
    <Modal open closeIcon onClose={onClose} size="tiny">
      <Modal.Header>
        Répartition numéraire de {formatMonetaire(montantTotalReglements, 2, true)}
      </Modal.Header>
      <Modal.Content scrolling={false}>
        {/* <span
          style={{
            fontWeight: 'bold',
          }}
        >
          La répartion numéraire est facultative.
        </span> */}
        <Table celled striped compact className="repartition-numeraire">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Nombre</Table.HeaderCell>
              <Table.HeaderCell>Valeur</Table.HeaderCell>
              <Table.HeaderCell>Montant</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {_.map(montants.slice(0, 6), (montant, index) => (
              <Table.Row key={`montant-${montant.valeur}`}>
                <Table.Cell warning textAlign="center" onKeyDown={handleKeyPress}>
                  <Currency
                    id={`currency@${montant.enterIndex}`}
                    data-testid={`currency-numeraire-${montant.enterIndex}`}
                    {...(index === 0 && { autoFocus: true })}
                    fluid
                    transparent
                    valueMin={0}
                    description=""
                    precision={0}
                    value={montant.nombre === '' ? null : montant.nombre}
                    onBlur={(e, v) => handleChangeMontant(index, v)}
                    onFocus={(e) => {
                      clearTimeout(timeout);
                      timeout = setTimeout(() => {
                        e.target.select();
                      }, 0);

                      if (montant.nombre === 0) {
                        handleChangeMontant(index, null);
                      }
                    }}
                  />
                </Table.Cell>
                <Table.Cell textAlign="right">{formatMonetaire(montant.valeur, 0)}</Table.Cell>
                <Table.Cell textAlign="right">
                  {montant.montant > 0 ? formatMonetaire(montant.montant, 0) : ''}
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.Cell colSpan={2} textAlign="right">
                <b>Total des billets</b>
              </Table.Cell>
              <Table.Cell warning textAlign="right">
                <b>{formatMonetaire(totalBillets, 0)}</b>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell
                colSpan={3}
                style={{
                  padding: 2,
                  height: 0,
                }}
              />
            </Table.Row>
            {_.map(montants.slice(6), (montant, index) => (
              <Table.Row key={`montant-${montant.valeur}`}>
                <Table.Cell warning textAlign="center" onKeyDown={handleKeyPress}>
                  <Currency
                    id={`currency@${montant.enterIndex}`}
                    data-testid={`currency-numeraire-${montant.enterIndex}`}
                    fluid
                    transparent
                    valueMin={0}
                    precision={0}
                    description=""
                    value={montant.nombre === '' ? null : montant.nombre}
                    onBlur={(e, v) => handleChangeMontant(index + 6, v)}
                    onFocus={(e) => {
                      clearTimeout(timeout);
                      timeout = setTimeout(() => {
                        e.target.select();
                      }, 0);

                      if (montant.nombre === 0) {
                        handleChangeMontant(index + 6, null);
                      }
                    }}
                  />
                </Table.Cell>
                <Table.Cell textAlign="right">{formatMonetaire(montant.valeur, 2)}</Table.Cell>
                <Table.Cell textAlign="right">
                  {montant.montant > 0 ? formatMonetaire(montant.montant, 2) : ''}
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.Cell colSpan={2} textAlign="right">
                <b>Total des pièces</b>
              </Table.Cell>
              <Table.Cell warning textAlign="right">
                <b>{formatMonetaire(totalPieces, 2)}</b>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell
                colSpan={3}
                style={{
                  padding: 2,
                  height: 0,
                }}
              />
            </Table.Row>
            <Table.Row>
              <Table.Cell colSpan={2} textAlign="right">
                <b>Total du versement</b>
              </Table.Cell>
              <Table.Cell
                {...(montantTotalReglements === totalVersement
                  ? { positive: true }
                  : { warning: true })}
                textAlign="right"
              >
                <b>{formatMonetaire(totalVersement, 2)}</b>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        <Message info size="tiny">
          <Message.Content>
            <Message.Header>
              Répartition facultative du numéraire à verser (hors fonds de caisse).
            </Message.Header>
          </Message.Content>
        </Message>
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="button-fermer-repartition-numeraire"
          onClick={onClose}
          size="tiny"
          basic
          icon="close"
          content="Ignorer la répartition"
        />
        <Button
          data-testid="button-valider-repartition-numeraire"
          onClick={handleValidate}
          positive
          icon="check"
          content="Confirmer la répartition"
          disabled={montantTotalReglements !== totalVersement}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRepartitionNumeraire;
